import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getRoleById, saveRole, updateRole, getModules } from "../Apis/RoleAPIs";

const RoleFormLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    roleName: "",
    status: true,
    permissions: [],
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      const modRes = await getModules();
      let modules = [];
      const modData = modRes.response?.data || modRes.response;
      if (modRes.status && Array.isArray(modData)) {
        modules = modData.map(mod => ({
          module: typeof mod === 'string' ? mod : mod.module || mod.name,
          view: false,
          add: false,
          edit: false,
          delete: false
        }));
      } else {
        // Fallback if API fails
        modules = [
          "dashboard", "employees", "roles", "products", "clients", 
          "flash-news", "location", "upcoming_expo", "present_expo", "past_expo", "testimonials"
        ].map(mod => ({ module: mod, view: false, add: false, edit: false, delete: false }));
      }

      if (isEdit) {
        const roleRes = await getRoleById(id);
        if (roleRes.status && roleRes.response) {
          const role = roleRes.response?.data || roleRes.response;
          // Merge with fetched modules to ensure all are present
          const existingPermissions = role.permissions || [];
          const mergedPermissions = modules.map(m => {
            const existing = existingPermissions.find(p => p.module === m.module);
            return existing ? existing : m;
          });
          setFormData({
            ...role,
            permissions: mergedPermissions
          });
        }
      } else {
        setFormData(prev => ({ ...prev, permissions: modules }));
      }
      setLoading(false);
    };

    fetchInitialData();
  }, [isEdit, id]);

  const handleToggleAll = (type, value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.map((p) => ({ ...p, [type]: value })),
    }));
  };

  const handlePermissionChange = (index, type, value) => {
    setFormData((prev) => {
      const updated = [...prev.permissions];
      updated[index][type] = value;
      return { ...prev, permissions: updated };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.roleName.trim()) newErrors.roleName = "Role Name is Required";
    // if (!formData.roleType) newErrors.roleType = "Role Type is Required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    let res;
    if (isEdit) {
      res = await updateRole(id, formData);
    } else {
      res = await saveRole(formData);
    }

    if (res.status) {
      toast.success(`Role ${isEdit ? "Updated" : "Added"} Successfully`);
      navigate("/roles");
    }
    setSubmitting(false);
  };

  const roleTypeOptions = [
    { value: "Super Admin", label: "Super Admin" },
    { value: "Admin", label: "Admin" },
    { value: "Manager", label: "Manager" },
    { value: "Staff", label: "Staff" },
  ];

  if (loading) {
    return (
        <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden">
      <div
        className="card-header border-bottom px-24 py-16 d-flex align-items-center justify-content-between"
        style={{ background: "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)" }}
      >
        <h6 className="text-white mb-0 fw-bold">{isEdit ? "Edit Role" : "Create Role"}</h6>
        {/* <Link to="/roles" className="btn btn-sm btn-light radius-8 fw-bold">
          Back to List
        </Link> */}
         <button 
                        onClick={() => navigate(-1)} 
                         className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm transition-all hover-scale-105"
                      style={{ fontSize: "12px", backgroundColor: "#f7e7da" }}
                    >
                       <Icon icon="solar:arrow-left-bold" />
                         Back to List
                    </button>
      </div>

      <div className="card-body p-32">
        <form onSubmit={handleSubmit}>
          <div className="row gy-4 mb-40">
            <div className="col-md-6">
              <label className="form-label fw-bold mb-8">
                Role Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control radius-8 py-11 ${errors.roleName ? "is-invalid" : ""}`}
                placeholder="Enter Role Name"
                value={formData.roleName}
                onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
              />
              {errors.roleName && <div className="invalid-feedback">{errors.roleName}</div>}
            </div>

            {/* <div className="col-md-6">
              <label className="form-label fw-bold mb-8">
                Role Type <span className="text-danger">*</span>
              </label>
              <Select
                options={roleTypeOptions}
                value={roleTypeOptions.find((o) => o.value === formData.roleType)}
                onChange={(val) => setFormData({ ...formData, roleType: val.value })}
                styles={selectStyles(errors.roleType)}
                placeholder="Select Role Type"
              />
              {errors.roleType && <div className="text-danger mt-1 small">{errors.roleType}</div>}
            </div> */}
          </div>

          <h6 className="mb-24 fw-bold text-primary-light">Role Permissions</h6>

          <div className="table-responsive radius-12 border shadow-sm">
            <table className="table mb-0 align-middle">
              <thead style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}>
                <tr>
                  <th className="text-white py-16 px-24 fw-bold" style={{ width: "40%" }}>Module's</th>
                  <th className="text-white py-16 px-16 text-center">
                    <div className="d-flex flex-column align-items-center gap-2">
                       <input 
                         type="checkbox" 
                         className="form-check-input mt-0 shadow-none cursor-pointer" 
                         style={{ width: '20px', height: '20px' }}
                         onChange={(e) => handleToggleAll('view', e.target.checked)}
                       />
                       <span className="fw-bold" style={{ fontSize: '13px' }}>View</span>
                    </div>
                  </th>
                  <th className="text-white py-16 px-16 text-center">
                    <div className="d-flex flex-column align-items-center gap-2">
                       <input 
                         type="checkbox" 
                         className="form-check-input mt-0 shadow-none cursor-pointer" 
                         style={{ width: '20px', height: '20px' }}
                         onChange={(e) => handleToggleAll('add', e.target.checked)}
                       />
                       <span className="fw-bold" style={{ fontSize: '13px' }}>Add</span>
                    </div>
                  </th>
                  <th className="text-white py-16 px-16 text-center">
                    <div className="d-flex flex-column align-items-center gap-2">
                       <input 
                         type="checkbox" 
                         className="form-check-input mt-0 shadow-none cursor-pointer" 
                         style={{ width: '20px', height: '20px' }}
                         onChange={(e) => handleToggleAll('edit', e.target.checked)}
                       />
                       <span className="fw-bold" style={{ fontSize: '13px' }}>Edit</span>
                    </div>
                  </th>
                  <th className="text-white py-16 px-16 text-center">
                    <div className="d-flex flex-column align-items-center gap-2">
                       <input 
                         type="checkbox" 
                         className="form-check-input mt-0 shadow-none cursor-pointer" 
                         style={{ width: '20px', height: '20px' }}
                         onChange={(e) => handleToggleAll('delete', e.target.checked)}
                       />
                       <span className="fw-bold" style={{ fontSize: '13px' }}>Delete</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.permissions.map((perm, index) => (
                  <tr key={perm.module} className="border-bottom">
                    <td className="py-16 px-24 fw-semibold text-secondary-light">{perm.module}</td>
                    {['view', 'add', 'edit', 'delete'].map((type) => (
                       <td key={type} className="py-16 px-16 text-center">
                          <input
                            type="checkbox"
                            className="form-check-input shadow-none cursor-pointer"
                            style={{ width: '18px', height: '18px' }}
                            checked={perm[type]}
                            onChange={(e) => handlePermissionChange(index, type, e.target.checked)}
                          />
                       </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-40 d-flex justify-content-end gap-3">
            <Link to="/roles" className="btn btn-outline-secondary radius-8 px-32 py-11 fw-bold">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary radius-8 px-40 py-11 fw-bold"
              style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleFormLayer;
